import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  Briefcase,
  Plane,
  Hotel,
  KeyRound,
  Headphones,
  ChevronDown,
  Pencil,
  X,
  ArrowRight,
  Mail,
  Phone,
  Cake,
  Building2,
  ShieldCheck,
  Clock,
} from "lucide-react";

const FLIGHT_BOOKINGS_ROUTE = "/flight-bookings";
const HOTEL_BOOKINGS_ROUTE = "/hotel-bookings";

const navItems = [
  { key: "profile", label: "My Profile", icon: User },
  {
    key: "bookings",
    label: "My Bookings",
    icon: Briefcase,
    hasChildren: true,
    children: [
      { key: "flights", label: "Flight Bookings", icon: Plane, route: FLIGHT_BOOKINGS_ROUTE },
      { key: "hotels", label: "Hotel Bookings", icon: Hotel, route: HOTEL_BOOKINGS_ROUTE },
    ],
  },
  { key: "reset", label: "Reset Password", icon: KeyRound },
  { key: "support", label: "Support", icon: Headphones },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "2000-05-14" / "2000-05-14T00:00:00" -> "14 May 2000"
const formatDisplayDate = (value) => {
  if (!value) return "";
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(value);
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${m[3]} ${month} ${m[1]}` : String(value);
};

const getInitials = (fullName) => {
  const cleaned = (fullName || "")
    .replace(/^(mr|mrs|ms)\.?\s+/i, "")
    .trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// ===================== One row of profile info =====================
function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="pp-info">
      <span className="pp-info__icon">
        <Icon size={16} />
      </span>
      <div className="pp-info__text">
        <span className="pp-info__label">{label}</span>
        <span className={`pp-info__value${value ? "" : " pp-info__value--empty"}`}>
          {value || "Not added"}
        </span>
      </div>
    </div>
  );
}

// ===================== My Bookings landing (two boxes) =====================
function MyBookingsContent() {
  const navigate = useNavigate();

  return (
    <>
      <div className="pp-panel__head">
        <div>
          <h2 className="pp-panel__title">My Bookings</h2>
          <p className="pp-panel__sub">
            View and manage your flight and hotel booking history
          </p>
        </div>
      </div>

      <div className="pp-bookings">
        <button
          type="button"
          className="pp-bk pp-bk--flight"
          onClick={() => navigate(FLIGHT_BOOKINGS_ROUTE)}
        >
          <span className="pp-bk__icon">
            <Plane size={26} />
          </span>
          <span className="pp-bk__body">
            <strong>Flight Bookings</strong>
            <span>View your past and upcoming flight reservations</span>
          </span>
          <span className="pp-bk__arrow">
            <ArrowRight size={18} />
          </span>
        </button>

        <button
          type="button"
          className="pp-bk pp-bk--hotel"
          onClick={() => navigate(HOTEL_BOOKINGS_ROUTE)}
        >
          <span className="pp-bk__icon">
            <Hotel size={26} />
          </span>
          <span className="pp-bk__body">
            <strong>Hotel Bookings</strong>
            <span>View your past and upcoming hotel reservations</span>
          </span>
          <span className="pp-bk__arrow">
            <ArrowRight size={18} />
          </span>
        </button>
      </div>
    </>
  );
}

// ===================== Placeholder for tabs not built yet =====================
function ComingSoon({ title, icon: Icon }) {
  return (
    <>
      <div className="pp-panel__head">
        <div>
          <h2 className="pp-panel__title">{title}</h2>
        </div>
      </div>
      <div className="pp-soon">
        <span className="pp-soon__icon">
          <Icon size={28} />
        </span>
        <h3>{title} is coming soon</h3>
        <p>We're working on this section. Please check back shortly.</p>
      </div>
    </>
  );
}

// ===================== Edit Personal Information Modal =====================
function EditPersonalInfoModal({ profile, onClose, onSave }) {
  const parseFullName = (fullName) => {
    const parts = (fullName || "").trim().split(" ");
    let title = "Mr.";
    let rest = parts;

    if (["Mr", "Mr.", "Ms", "Ms.", "Mrs", "Mrs."].includes(parts[0])) {
      title = parts[0].endsWith(".") ? parts[0] : `${parts[0]}.`;
      rest = parts.slice(1);
    }

    const firstName = rest[0] || "";
    const lastName = rest.slice(1).join(" ") || "";

    return { title, firstName, lastName };
  };

  const initial = parseFullName(profile.fullName);

  const [title, setTitle] = useState(initial.title);
  const [firstName, setFirstName] = useState(initial.firstName);
  const [lastName, setLastName] = useState(initial.lastName);
  const [dob, setDob] = useState(profile.dob || "");

  const handleClear = () => {
    setFirstName("");
    setLastName("");
    setDob("");
  };

  const handleUpdate = () => {
    const updated = {
      title,
      firstName,
      lastName,
      dob,
      fullName: `${title} ${firstName} ${lastName}`.trim(),
    };
    console.log("Updating personal info:", updated);
    if (onSave) onSave(updated);
    onClose();
  };

  return (
    <div className="pp-overlay" onClick={onClose}>
      <div className="pp-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="pp-modal__head">
          <div>
            <h3>Update personal information</h3>
            <p>Keep your details accurate for smooth bookings.</p>
          </div>
          <button type="button" className="pp-modal__close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="pp-modal__body">
          <div className="pp-seg">
            {["Mr.", "Ms.", "Mrs."].map((t) => (
              <button
                key={t}
                type="button"
                className={`pp-seg__btn${title === t ? " pp-seg__btn--active" : ""}`}
                onClick={() => setTitle(t)}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="pp-form-row">
            <div className="pp-form-field">
              <label htmlFor="firstName">First name</label>
              <input
                id="firstName"
                type="text"
                placeholder="e.g. Aarav"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="pp-form-field">
              <label htmlFor="lastName">Last name</label>
              <input
                id="lastName"
                type="text"
                placeholder="e.g. Sharma"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          <div className="pp-form-field">
            <label htmlFor="dob">Date of birth</label>
            <input
              id="dob"
              type="date"
              value={dob}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setDob(e.target.value)}
            />
          </div>
        </div>

        <div className="pp-modal__foot">
          <button type="button" className="pp-btn pp-btn--ghost" onClick={handleClear}>
            Clear
          </button>
          <button type="button" className="pp-btn pp-btn--primary" onClick={handleUpdate}>
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProfilePage({ userSession }) {
  const navigate = useNavigate();
  const [active, setActive] = useState("profile");
  const [bookingsOpen, setBookingsOpen] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const [profile, setProfile] = useState({
    email: userSession?.UserEmail || "demo45@gmail.com",
    fullName: userSession?.UserName || "Mr Demo Test",
    mobile: userSession?.UserMobile || "9632587410",
    gstin: userSession?.GSTIN || "",
    dob: userSession?.UserDOB || "",
  });

  const handleSaveProfile = (updated) => {
    setProfile((prev) => ({
      ...prev,
      fullName: updated.fullName,
      dob: updated.dob,
    }));
  };

  const renderMainContent = () => {
    if (active === "bookings") return <MyBookingsContent />;
    if (active === "reset") return <ComingSoon title="Reset Password" icon={KeyRound} />;
    if (active === "support") return <ComingSoon title="Support" icon={Headphones} />;

    return (
      <>
        <div className="pp-panel__head">
          <div>
            <h2 className="pp-panel__title">My Profile</h2>
            <p className="pp-panel__sub">Manage your personal and business details</p>
          </div>
        </div>

        <div className="pp-cards">
          {/* Login information */}
          <section className="pp-card">
            <div className="pp-card__head">
              <h3>
                <ShieldCheck size={17} /> Login information
              </h3>
            </div>
            <div className="pp-card__body">
              <InfoRow icon={Mail} label="Email address" value={profile.email} />
            </div>
          </section>

          {/* Personal information */}
          <section className="pp-card">
            <div className="pp-card__head">
              <h3>
                <User size={17} /> Personal information
              </h3>
              <button type="button" className="pp-edit" onClick={() => setIsEditOpen(true)}>
                <Pencil size={13} /> Edit
              </button>
            </div>
            <div className="pp-card__body pp-card__body--grid">
              <InfoRow icon={User} label="Full name" value={profile.fullName} />
              <InfoRow icon={Phone} label="Mobile number" value={profile.mobile} />
              <InfoRow icon={Cake} label="Date of birth" value={formatDisplayDate(profile.dob)} />
            </div>
          </section>

          {/* GSTIN */}
          <section className="pp-card">
            <div className="pp-card__head">
              <h3>
                <Building2 size={17} /> GSTIN details
              </h3>
            </div>
            <div className="pp-card__body">
              <InfoRow icon={Building2} label="GSTIN number" value={profile.gstin} />
            </div>
          </section>
        </div>
      </>
    );
  };

  return (
    <>
      <PPStyle />
      <div className="pp-page">
        {/* ---------- Hero ---------- */}
        <header className="pp-hero">
          <div className="pp-hero__inner">
            <div className="pp-avatar">{getInitials(profile.fullName)}</div>
            <div className="pp-hero__text">
              <p className="pp-hero__hello">Welcome back</p>
              <h1 className="pp-hero__name">{profile.fullName}</h1>
              <div className="pp-hero__meta">
                <span>
                  <Mail size={14} /> {profile.email}
                </span>
                {profile.mobile && (
                  <span>
                    <Phone size={14} /> +91 {profile.mobile}
                  </span>
                )}
              </div>
            </div>
          </div>
        </header>

        <div className="pp-shell">
          {/* ---------- Sidebar ---------- */}
          <aside className="pp-side">
            <nav className="pp-nav" aria-label="Account">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = active === item.key;
                return (
                  <div key={item.key} className="pp-nav__group">
                    <button
                      type="button"
                      className={`pp-nav__item${isActive ? " pp-nav__item--active" : ""}`}
                      onClick={() => {
                        setActive(item.key);
                        if (item.hasChildren) setBookingsOpen((o) => !o);
                      }}
                    >
                      <span className="pp-nav__left">
                        <Icon size={17} />
                        {item.label}
                      </span>
                      {item.hasChildren && (
                        <ChevronDown
                          size={16}
                          className={`pp-nav__chev${bookingsOpen ? " pp-nav__chev--open" : ""}`}
                        />
                      )}
                    </button>

                    {item.hasChildren && bookingsOpen && (
                      <div className="pp-nav__children">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          return (
                            <button
                              key={child.key}
                              type="button"
                              className="pp-nav__item pp-nav__child"
                              onClick={() => navigate(child.route)}
                            >
                              <span className="pp-nav__left">
                                <ChildIcon size={15} />
                                {child.label}
                              </span>
                              <ArrowRight size={14} className="pp-nav__go" />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            <div className="pp-help">
              <Clock size={16} />
              <div>
                <strong>Need help?</strong>
                <span>Our support team is here for you.</span>
              </div>
            </div>
          </aside>

          {/* ---------- Main ---------- */}
          <main className="pp-main">{renderMainContent()}</main>
        </div>

        {isEditOpen && (
          <EditPersonalInfoModal
            profile={profile}
            onClose={() => setIsEditOpen(false)}
            onSave={handleSaveProfile}
          />
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Styles — scoped under .pp-*
// ---------------------------------------------------------------------------
function PPStyle() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&family=Inter:wght@400;500;600;700&display=swap');

      .pp-page, .pp-page *, .pp-page *::before, .pp-page *::after { box-sizing: border-box; }

      .pp-page {
        --ink: #16215C;
        --ink-soft: #26389E;
        --accent: #E4127F;
        --accent-deep: #B80E64;
        --orange: #ea580c;
        --paper: #F4F6FB;
        --card: #FFFFFF;
        --line: #E1E5F2;
        --text: #1A1F36;
        --text-soft: #667085;

        min-height: 100vh;
        background: var(--paper);
        font-family: 'Inter', sans-serif;
        color: var(--text);
        padding-bottom: 64px;
      }

      /* Hero */
      .pp-hero {
        background: linear-gradient(120deg, #0d2b6b 0%, #1a4fa0 55%, #2a63c4 100%);
        color: #fff;
        padding: 36px 20px 96px;
        position: relative;
        overflow: hidden;
      }
      .pp-hero::after {
        content: ""; position: absolute; right: -80px; top: -90px;
        width: 300px; height: 300px; border-radius: 50%;
        background: radial-gradient(circle, rgba(228,18,127,0.35), rgba(228,18,127,0) 70%);
      }
      .pp-hero__inner {
        max-width: 1080px; margin: 0 auto; position: relative; z-index: 1;
        display: flex; align-items: center; gap: 20px;
      }
      .pp-avatar {
        width: 76px; height: 76px; border-radius: 50%; flex-shrink: 0;
        display: flex; align-items: center; justify-content: center;
        background: linear-gradient(135deg, var(--accent), #ff5aa8);
        border: 3px solid rgba(255,255,255,0.5);
        font-family: 'Fraunces', serif; font-size: 28px; font-weight: 600;
        box-shadow: 0 8px 24px rgba(0,0,0,0.2);
      }
      .pp-hero__text { min-width: 0; }
      .pp-hero__hello { margin: 0; font-size: 13px; color: rgba(255,255,255,0.78); }
      .pp-hero__name {
        margin: 2px 0 8px; font-family: 'Fraunces', serif;
        font-size: 28px; font-weight: 600; line-height: 1.15; overflow-wrap: anywhere;
      }
      .pp-hero__meta { display: flex; flex-wrap: wrap; gap: 6px 20px; font-size: 13px; color: rgba(255,255,255,0.88); }
      .pp-hero__meta span { display: inline-flex; align-items: center; gap: 6px; overflow-wrap: anywhere; }

      /* Shell */
      .pp-shell {
        max-width: 1080px; margin: -60px auto 0; padding: 0 20px;
        display: grid; grid-template-columns: 270px 1fr; gap: 20px;
        align-items: start; position: relative; z-index: 2;
      }

      /* Sidebar */
      .pp-side { display: flex; flex-direction: column; gap: 14px; position: sticky; top: 20px; }
      .pp-nav {
        background: var(--card); border: 1px solid var(--line); border-radius: 16px;
        box-shadow: 0 6px 24px rgba(22,33,92,0.07);
        padding: 10px; display: flex; flex-direction: column; gap: 2px;
      }
      .pp-nav__item {
        width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 10px;
        padding: 12px 14px; border: none; border-radius: 10px;
        background: transparent; color: var(--text-soft);
        font-family: inherit; font-size: 14px; font-weight: 500;
        cursor: pointer; text-align: left;
        transition: background 0.15s ease, color 0.15s ease;
      }
      .pp-nav__item:hover { background: #F4F6FB; color: var(--ink); }
      .pp-nav__item--active {
        background: linear-gradient(90deg, #EEF2FF, #F7F9FF);
        color: var(--ink); font-weight: 700;
        box-shadow: inset 3px 0 0 var(--accent);
      }
      .pp-nav__item--active svg { color: var(--accent); }
      .pp-nav__left { display: flex; align-items: center; gap: 12px; }
      .pp-nav__left svg { flex-shrink: 0; }
      .pp-nav__chev { transition: transform 0.2s ease; color: #98A2B3; }
      .pp-nav__chev--open { transform: rotate(180deg); }
      .pp-nav__children { display: flex; flex-direction: column; gap: 2px; margin: 2px 0 4px 18px; padding-left: 8px; border-left: 1.5px dashed var(--line); }
      .pp-nav__child { padding: 10px 12px; font-size: 13.5px; }
      .pp-nav__go { opacity: 0; transform: translateX(-4px); transition: all 0.15s ease; color: var(--accent); }
      .pp-nav__child:hover .pp-nav__go { opacity: 1; transform: translateX(0); }

      .pp-help {
        display: flex; align-items: center; gap: 12px;
        background: linear-gradient(135deg, #FFF1E6, #FDE8F3);
        border: 1px solid #F8D9E8; border-radius: 14px; padding: 14px 16px;
        color: var(--accent-deep);
      }
      .pp-help svg { flex-shrink: 0; }
      .pp-help div { display: flex; flex-direction: column; gap: 1px; }
      .pp-help strong { font-size: 13.5px; color: var(--text); }
      .pp-help span { font-size: 12px; color: var(--text-soft); }

      /* Main panel */
      .pp-main {
        background: var(--card); border: 1px solid var(--line); border-radius: 16px;
        box-shadow: 0 6px 24px rgba(22,33,92,0.07);
        padding: 26px 28px 30px; min-width: 0;
      }
      .pp-panel__head { margin-bottom: 20px; padding-bottom: 18px; border-bottom: 1px solid var(--line); }
      .pp-panel__title { margin: 0; font-family: 'Fraunces', serif; font-size: 24px; font-weight: 600; }
      .pp-panel__sub { margin: 4px 0 0; font-size: 13.5px; color: var(--text-soft); }

      /* Info cards */
      .pp-cards { display: flex; flex-direction: column; gap: 16px; }
      .pp-card { border: 1px solid var(--line); border-radius: 14px; overflow: hidden; }
      .pp-card__head {
        display: flex; align-items: center; justify-content: space-between;
        padding: 14px 20px; background: #F8F9FD; border-bottom: 1px solid var(--line);
      }
      .pp-card__head h3 {
        margin: 0; display: flex; align-items: center; gap: 9px;
        font-size: 14.5px; font-weight: 700; color: var(--ink);
      }
      .pp-card__head h3 svg { color: var(--accent); }
      .pp-card__body { padding: 8px 20px; }
      .pp-card__body--grid { display: grid; grid-template-columns: 1fr 1fr; column-gap: 28px; }
      .pp-card__body--grid .pp-info:nth-child(1) { grid-column: 1 / -1; }

      .pp-info { display: flex; align-items: center; gap: 14px; padding: 14px 0; border-bottom: 1px dashed var(--line); min-width: 0; }
      .pp-card__body > .pp-info:last-child,
      .pp-card__body--grid .pp-info:nth-last-child(-n+1) { border-bottom: none; }
      .pp-card__body:not(.pp-card__body--grid) .pp-info { border-bottom: none; }
      .pp-info__icon {
        width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0;
        display: flex; align-items: center; justify-content: center;
        background: #EEF2FF; color: var(--ink-soft);
      }
      .pp-info__text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
      .pp-info__label { font-size: 12px; color: var(--text-soft); }
      .pp-info__value { font-size: 14.5px; font-weight: 600; overflow-wrap: anywhere; }
      .pp-info__value--empty { color: #98A2B3; font-weight: 400; font-style: italic; }

      .pp-edit {
        display: inline-flex; align-items: center; gap: 6px;
        background: #fff; color: var(--ink-soft);
        border: 1px solid #C9D2F0; border-radius: 999px;
        padding: 6px 14px; font-size: 12.5px; font-weight: 700; font-family: inherit;
        cursor: pointer; transition: all 0.15s ease;
      }
      .pp-edit:hover { background: var(--ink-soft); color: #fff; border-color: var(--ink-soft); }

      /* Bookings landing */
      .pp-bookings { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
      .pp-bk {
        display: flex; align-items: center; gap: 16px; width: 100%;
        padding: 22px 20px; text-align: left; font-family: inherit;
        background: #fff; border: 1px solid var(--line); border-radius: 16px; cursor: pointer;
        transition: transform 0.15s ease, box-shadow 0.2s ease, border-color 0.2s ease;
      }
      .pp-bk:hover { transform: translateY(-3px); box-shadow: 0 14px 32px rgba(22,33,92,0.13); border-color: #C9D2F0; }
      .pp-bk__icon {
        width: 56px; height: 56px; border-radius: 16px; flex-shrink: 0;
        display: flex; align-items: center; justify-content: center;
      }
      .pp-bk--flight .pp-bk__icon { background: #EEF2FF; color: var(--ink-soft); }
      .pp-bk--hotel .pp-bk__icon { background: #FDE8F3; color: var(--accent); }
      .pp-bk__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
      .pp-bk__body strong { font-family: 'Fraunces', serif; font-size: 17px; font-weight: 600; color: var(--text); }
      .pp-bk__body span { font-size: 13px; color: var(--text-soft); line-height: 1.4; }
      .pp-bk__arrow { color: #98A2B3; flex-shrink: 0; transition: transform 0.2s ease, color 0.2s ease; }
      .pp-bk:hover .pp-bk__arrow { transform: translateX(4px); color: var(--accent); }

      /* Coming soon */
      .pp-soon { text-align: center; padding: 48px 20px; }
      .pp-soon__icon {
        width: 68px; height: 68px; border-radius: 50%; margin: 0 auto 16px;
        display: flex; align-items: center; justify-content: center;
        background: #EEF2FF; color: var(--ink-soft);
      }
      .pp-soon h3 { margin: 0 0 6px; font-family: 'Fraunces', serif; font-size: 20px; font-weight: 600; }
      .pp-soon p { margin: 0; font-size: 14px; color: var(--text-soft); }

      /* Modal */
      .pp-overlay {
        position: fixed; inset: 0; z-index: 1000;
        background: rgba(15, 23, 62, 0.55); backdrop-filter: blur(3px);
        display: flex; align-items: center; justify-content: center; padding: 16px;
        animation: pp-fade 0.15s ease;
      }
      .pp-modal {
        width: 100%; max-width: 480px; background: #fff; border-radius: 18px;
        box-shadow: 0 24px 60px rgba(15,23,62,0.3); overflow: hidden;
        animation: pp-pop 0.18s ease;
        font-family: 'Inter', sans-serif; color: var(--text);
      }
      @keyframes pp-fade { from { opacity: 0; } to { opacity: 1; } }
      @keyframes pp-pop { from { opacity: 0; transform: translateY(10px) scale(0.98); } to { opacity: 1; transform: none; } }
      .pp-modal__head {
        display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
        padding: 20px 24px 16px; border-bottom: 1px solid var(--line);
      }
      .pp-modal__head h3 { margin: 0; font-family: 'Fraunces', serif; font-size: 20px; font-weight: 600; }
      .pp-modal__head p { margin: 4px 0 0; font-size: 13px; color: var(--text-soft); }
      .pp-modal__close {
        width: 34px; height: 34px; border-radius: 50%; flex-shrink: 0;
        display: flex; align-items: center; justify-content: center;
        background: #F4F6FB; color: var(--text-soft); border: none; cursor: pointer;
        transition: background 0.15s ease;
      }
      .pp-modal__close:hover { background: #E4E8F5; }
      .pp-modal__body { padding: 20px 24px; display: flex; flex-direction: column; gap: 18px; }

      .pp-seg { display: inline-flex; background: #F4F6FB; border-radius: 10px; padding: 4px; align-self: flex-start; }
      .pp-seg__btn {
        border: none; background: transparent; padding: 8px 20px; border-radius: 8px;
        font-family: inherit; font-size: 13.5px; font-weight: 600; color: var(--text-soft); cursor: pointer;
        transition: all 0.15s ease;
      }
      .pp-seg__btn--active { background: #fff; color: var(--ink); box-shadow: 0 2px 8px rgba(22,33,92,0.12); }

      .pp-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
      .pp-form-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
      .pp-form-field label { font-size: 12px; font-weight: 600; color: var(--text-soft); letter-spacing: 0.3px; text-transform: uppercase; }
      .pp-form-field input {
        height: 46px; padding: 0 14px; width: 100%;
        border: 1.5px solid var(--line); border-radius: 10px; outline: none;
        font-family: inherit; font-size: 14px; color: var(--text); background: #fff;
        transition: border-color 0.15s ease, box-shadow 0.15s ease;
      }
      .pp-form-field input:focus { border-color: var(--ink-soft); box-shadow: 0 0 0 4px rgba(38,56,158,0.1); }
      .pp-form-field input::placeholder { color: #B7BDCC; }

      .pp-modal__foot {
        display: flex; justify-content: flex-end; gap: 10px;
        padding: 16px 24px 20px; border-top: 1px solid var(--line); background: #FAFBFE;
      }
      .pp-btn {
        height: 44px; padding: 0 24px; border-radius: 10px;
        font-family: inherit; font-size: 14px; font-weight: 700; cursor: pointer;
        transition: background 0.15s ease, transform 0.1s ease;
      }
      .pp-btn:active { transform: translateY(1px); }
      .pp-btn--primary { background: var(--orange); color: #fff; border: none; box-shadow: 0 6px 16px rgba(234,88,12,0.28); }
      .pp-btn--primary:hover { background: var(--accent-deep); }
      .pp-btn--ghost { background: #fff; color: var(--ink-soft); border: 1.5px solid #C9D2F0; }
      .pp-btn--ghost:hover { background: #EEF2FF; }

      /* Responsive */
      @media (max-width: 900px) {
        .pp-shell { grid-template-columns: 1fr; }
        .pp-side { position: static; }
        .pp-help { display: none; }
        .pp-nav { flex-direction: row; overflow-x: auto; gap: 6px; padding: 8px; -webkit-overflow-scrolling: touch; }
        .pp-nav__group { display: flex; flex-shrink: 0; align-items: center; gap: 6px; }
        .pp-nav__item { width: auto; white-space: nowrap; }
        .pp-nav__item--active { box-shadow: inset 0 -3px 0 var(--accent); }
        .pp-nav__children { flex-direction: row; margin: 0; padding: 0; border: none; }
        .pp-nav__go { display: none; }
      }
      @media (max-width: 560px) {
        .pp-hero { padding: 26px 14px 84px; }
        .pp-hero__inner { gap: 14px; }
        .pp-avatar { width: 60px; height: 60px; font-size: 22px; }
        .pp-hero__name { font-size: 22px; }
        .pp-shell { padding: 0 12px; }
        .pp-main { padding: 20px 16px 24px; }
        .pp-panel__title { font-size: 21px; }
        .pp-card__body { padding: 4px 16px; }
        .pp-card__body--grid { grid-template-columns: 1fr; }
        .pp-bookings { grid-template-columns: 1fr; }
        .pp-form-row { grid-template-columns: 1fr; }
        .pp-form-field input { font-size: 16px; }
        .pp-modal__foot .pp-btn { flex: 1; }
      }
    `}</style>
  );
}  